import { SYNC_KEY_PROYECTOS_CATALOGO } from "../../../constantes";
import { CatalogoProyectoItem, ProyectosPlataformaService } from "../../../shared/generated/grpc/plataforma_proyectos_pb";
import { CatalogoItemResponse, CatalogoTipoP } from "../../../shared/response/proyectos/CatalogoItemResponse";
import { CatalogoPrecioPayload } from "../../../shared/services/api/payload/proyectos/CatalogoPrecioPayload";
import { IListCache } from "../../../shared/services/base/IListCache";
import { serviceSync } from "../../../shared/services/base/ServiceSync";
import { grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";

/**
 * Catálogo de ítems por cultivo (`ProyectosPlataformaService`, gRPC). La lista en memoria se guarda por
 * cultivo. `precioVigente` viene calculado del servidor con el snapshot activo y la tasa de IPC de la
 * categoría en la cosecha vigente. Implementa `IListCache` para que `CatalogoItemSaveService` y
 * `CatalogoItemDeleteService` (el CRUD del catálogo maestro) reflejen sus cambios sin volver a pedir la lista.
 */
export class CatalogoService implements IListCache<CatalogoItemResponse> {
  static readonly instance = new CatalogoService();
  private constructor() {}

  private readonly _byCultivoId = new Map<string, CatalogoItemResponse[]>();
  /** Catálogo + ítems propios, por `cultivoId|clienteId`. Separada de `_byCultivoId` (ver `getByCultivoIdConPropios`). */
  private readonly _conPropios = new Map<string, CatalogoItemResponse[]>();

  /** Ítems del cultivo (`ListCatalogoByCultivo`); sin conexión devuelve lo último cargado. */
  async getByCultivoId(cultivoId: string): Promise<CatalogoItemResponse[]> {
    await serviceSync
      .run(`${SYNC_KEY_PROYECTOS_CATALOGO}/byCultivoId/${cultivoId}`, async () => {
        const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).listCatalogoByCultivo({ cultivoId }));
        this._byCultivoId.set(cultivoId, r.items.map(toCatalogoItemResponse));
      })
      .catch((error: unknown) => {
        // Sin conexión devuelve lo último cargado, pero el error queda en consola.
        console.warn(`[CatalogoService.getByCultivoId] no se pudo leer el catálogo del cultivo ${cultivoId}; se usa lo último cargado`, error);
      });
    return this._byCultivoId.get(cultivoId) ?? [];
  }

  /**
   * Ítems del cultivo **más los ítems propios de ese beneficiario** (`ListCatalogoByCultivo` con
   * `clienteId`). Lo usa el wizard; la pantalla de Catálogo sigue usando `getByCultivoId`, que trae solo
   * el maestro.
   *
   * Cache **aparte** de `_byCultivoId`, a propósito: esa lista la comparte la pantalla del catálogo
   * maestro, y si los ítems propios de un beneficiario cayeran ahí, aparecerían en el catálogo de todos
   * -el mismo tipo de fuga que el `es_propio` del servidor existe para evitar-.
   */
  async getByCultivoIdConPropios(cultivoId: string, clienteId: string): Promise<CatalogoItemResponse[]> {
    const key = `${cultivoId}|${clienteId}`;
    await serviceSync
      .run(`${SYNC_KEY_PROYECTOS_CATALOGO}/conPropios/${key}`, async () => {
        const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).listCatalogoByCultivo({ cultivoId, clienteId }));
        this._conPropios.set(key, r.items.map(toCatalogoItemResponse));
      })
      .catch((error: unknown) => {
        // Sin conexión devuelve lo último cargado, pero el error queda en consola.
        console.warn(`[CatalogoService.getByCultivoIdConPropios] no se pudo leer el catálogo con propios de ${key}; se usa lo último cargado`, error);
      });
    return this._conPropios.get(key) ?? [];
  }

  /** Olvida la lista con propios de ese beneficiario, para que la próxima vez se pida de nuevo. */
  invalidarConPropios(cultivoId: string, clienteId: string): void {
    this._conPropios.delete(`${cultivoId}|${clienteId}`);
  }

  /** Cambia el precio referencial (`ActualizarPrecioCatalogo`); null = sin precio. El tipo de precio no cambia. */
  async updatePrecio(id: string, payload: CatalogoPrecioPayload): Promise<CatalogoItemResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).actualizarPrecioCatalogo({
      id,
      precioReferencial: payload.precio !== null ? BigInt(payload.precio) : undefined,
    }));
    const item = toCatalogoItemResponse(r);
    this.upsert(item);
    return item;
  }

  /** Activa o desactiva el ítem (`ToggleActivoCatalogo`). */
  async toggleActivo(id: string): Promise<CatalogoItemResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).toggleActivoCatalogo({ id }));
    const item = toCatalogoItemResponse(r);
    this.upsert(item);
    return item;
  }

  // ── IListCache ──────────────────────────────────────────────

  /** Reemplaza o agrega el ítem en la lista de su cultivo, si esa lista ya está cargada. */
  upsert(item: CatalogoItemResponse): void {
    const items = this._byCultivoId.get(item.cultivoId);
    if (!items) return;
    const index = items.findIndex((x) => x.id === item.id);
    if (index >= 0) items[index] = item;
    else items.push(item);
  }

  /** Quita el ítem de la lista de cualquier cultivo cargado. */
  remove(id: string): void {
    this._byCultivoId.forEach((items, cultivoId) => {
      this._byCultivoId.set(cultivoId, items.filter((x) => x.id !== id));
    });
  }
}

/** Mensaje gRPC del ítem → `CatalogoItemResponse`. Lo reusa el guardado del catálogo maestro. */
export function toCatalogoItemResponse(i: CatalogoProyectoItem): CatalogoItemResponse {
  return {
    id: i.id,
    isEliminado: false,
    cultivoId: i.cultivoId,
    cultivoNombre: i.cultivoNombre,
    sec: i.sec,
    sub: i.sub,
    tipo: i.tipo,
    nombre: i.nombre,
    categoria: i.categoria,
    unidad: i.unidad,
    cantUnidad: i.cantUnidad,
    cantidadPorHa: i.cantidadPorHa,
    tipoP: i.tipoP as CatalogoTipoP,
    precioReferencial: i.precioReferencial !== undefined ? Number(i.precioReferencial) : null,
    precioVigente: i.precioVigente !== undefined ? Number(i.precioVigente) : null,
    activo: i.activo,
    nitro: i.nitro,
    freq: i.freq,
    freqUnidad: i.freqUnidad ?? null,
    incl: i.incl as CatalogoItemResponse["incl"],
    faseId: i.faseId ?? null,
    faseCodigo: i.faseCodigo ?? null,
    esPropio: i.esPropio,
    creadoPorNombre: i.creadoPorNombre ?? null,
    duenos: i.duenos,
    motivo: i.motivo ?? null,
  };
}
